import { useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { CONFLITOS_AUTORIZAVEIS, FORMA_PAGAMENTO_ROTULO, formatarDataHora, formatarReais } from "@caixaflow/shared";
import { CamposAutorizacao, useAutorizacaoAdmin } from "../../components/AutorizacaoAdmin";
import { mensagemDeErro } from "../../components/Estados";
import { Modal } from "../../components/Modal";
import { useToast } from "../../components/Toast";
import { autorizarVendaRecusada, tentarDeNovo } from "../../offline/envio";
import type { VendaNaFila } from "../../offline/fila";
import type { useFila } from "../../offline/useFila";

const DICA_POR_CODIGO: Record<string, string> = {
  CAIXA_FECHADO: "O caixa foi fechado em outro aparelho. Peça a um ADMIN para reabri-lo e toque em “Tentar de novo”.",
  ESTOQUE_INSUFICIENTE: "Peça a um ADMIN para lançar a entrada de estoque e tente de novo, ou autorize com o ADMIN.",
  PRECO_DIVERGENTE: "O preço mudou depois da venda. Um ADMIN pode autorizar a venda com o preço que foi cobrado.",
  FORA_DO_CARDAPIO: "O produto saiu do cardápio depois da venda. Um ADMIN pode autorizar a venda.",
};

type Fila = ReturnType<typeof useFila>;

/** Vendas guardadas no aparelho: pendentes de envio e recusadas pelo servidor. */
export function PainelFila({ aberto, onFechar, fila }: { aberto: boolean; onFechar: () => void; fila: Fila }) {
  const [autorizando, setAutorizando] = useState<VendaNaFila | null>(null);
  const toast = useToast();

  const enviarAgora = async () => {
    const r = await fila.sincronizar();
    if (r.semConexao) toast("Ainda sem conexão com o servidor.", "erro");
    else toast(`${r.enviadas} enviada(s)${r.recusadas ? ` · ${r.recusadas} recusada(s)` : ""}`, r.recusadas ? "erro" : "ok");
  };

  return (
    <Modal
      aberto={aberto}
      titulo={autorizando ? "Autorizar venda" : "Vendas guardadas no aparelho"}
      onFechar={() => (autorizando ? setAutorizando(null) : onFechar())}
    >
      {autorizando ? (
        <FormAutorizar
          venda={autorizando}
          onVoltar={() => setAutorizando(null)}
          onAutorizada={() => {
            // Resolvida: volta direto ao PDV (se houver outras recusadas, o aviso continua na tela).
            setAutorizando(null);
            onFechar();
          }}
        />
      ) : (
        <div className="form">
          {fila.itens.length === 0 && <div className="vazio">Nenhuma venda guardada. Tudo foi enviado.</div>}
          {fila.pendentes.length > 0 && (
            <div className="linha">
              <span className="mudo pequeno">
                {fila.pendentes.length} pendente(s). O envio é automático quando a internet volta.
              </span>
              <button className="btn pequeno primario" onClick={enviarAgora} disabled={fila.sincronizando}>
                {fila.sincronizando ? "Enviando…" : "Enviar agora"}
              </button>
            </div>
          )}
          {fila.itens.map((v) => (
            <div key={v.id} className="card pad" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <div className="linha">
                <b>{formatarReais(v.resumo.totalCentavos)}</b>
                <span className={`chip ${v.estado === "RECUSADA" ? "perigo" : "alerta"}`}>
                  {v.estado === "RECUSADA" ? "recusada" : "pendente"}
                </span>
              </div>
              <span className="pequeno">
                {v.resumo.itens.map((i) => `${i.quantidade}× ${i.nome}`).join(", ")} ·{" "}
                {FORMA_PAGAMENTO_ROTULO[v.resumo.formaPagamento]}
              </span>
              <span className="mudo pequeno">Registrada no aparelho em {formatarDataHora(v.criadaLocalEm)}</span>
              {v.erro && (
                <div className="aviso erro pequeno">
                  {v.erro.mensagem}
                  {DICA_POR_CODIGO[v.erro.codigo] && <div style={{ marginTop: 4 }}>{DICA_POR_CODIGO[v.erro.codigo]}</div>}
                </div>
              )}
              {v.estado === "RECUSADA" && (
                <div className="linha-esq">
                  <button
                    className="btn pequeno fantasma"
                    onClick={async () => {
                      await tentarDeNovo(v.id);
                      await enviarAgora();
                    }}
                  >
                    Tentar de novo
                  </button>
                  {(CONFLITOS_AUTORIZAVEIS as readonly string[]).includes(v.erro?.codigo ?? "") && (
                    <button className="btn pequeno primario" onClick={() => setAutorizando(v)}>
                      Autorizar com ADMIN
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

function FormAutorizar({ venda, onVoltar, onAutorizada }: { venda: VendaNaFila; onVoltar: () => void; onAutorizada: () => void }) {
  const autorizacao = useAutorizacaoAdmin();
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const qc = useQueryClient();
  const toast = useToast();

  const enviar = async (e: FormEvent) => {
    e.preventDefault();
    setEnviando(true);
    setErro(null);
    try {
      await autorizarVendaRecusada(venda, autorizacao.valor);
      toast("Venda aceita com autorização do ADMIN");
      qc.invalidateQueries({ queryKey: ["vendas", venda.caixaId] });
      qc.invalidateQueries({ queryKey: ["cardapio", venda.eventoId] });
      onAutorizada();
    } catch (falha) {
      setErro(mensagemDeErro(falha));
      autorizacao.limparSegredo();
    } finally {
      setEnviando(false);
    }
  };

  return (
    <form className="form" onSubmit={enviar} autoComplete="off">
      <p style={{ margin: 0 }}>
        Venda de <b>{formatarReais(venda.resumo.totalCentavos)}</b> ({venda.resumo.itens.map((i) => `${i.quantidade}× ${i.nome}`).join(", ")}).
      </p>
      <p className="mudo pequeno" style={{ margin: 0 }}>
        A venda é aceita como foi feita no balcão (preço cobrado e quantidade entregue), mesmo deixando o estoque negativo. A
        divergência e o ADMIN que autorizou ficam registrados.
      </p>
      {erro && (
        <div className="aviso erro" role="alert">
          {erro}
        </div>
      )}
      <CamposAutorizacao autorizacao={autorizacao} />
      <div className="modal-acoes">
        <button type="button" className="btn fantasma" onClick={onVoltar}>
          Voltar
        </button>
        <button type="submit" className="btn primario" disabled={enviando}>
          {enviando ? "Conferindo…" : "Autorizar venda"}
        </button>
      </div>
    </form>
  );
}
